/**
 * utilities/richText.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE Quill configuration for every product-description editor in the
 * dashboard (vendor product forms + admin QC). This is the standardization
 * contract: vendors can only produce the formatting the storefront renders,
 * so what they see at creation time is exactly what customers see.
 *
 * The server (utils/richDescription.js) is the authority — it re-sanitizes
 * the HTML to the same allowlist and derives the plain-text `description`
 * for the mobile apps. The helpers here just keep the UX honest.
 */

/* ── Canonical description typography (the WYSIWYG half of the contract) ──
   ONE spec for how a product description renders, everywhere. The authority
   is the CUSTOMER-facing storefront (Lusuku_Web RetailerWholesalerProductPage
   `RichDescription`); the editor and the quick-view modal mirror it. If you
   change a value here, change it there too (and vice versa):
     body: Urbanist/Inter 13.5px / 1.65 #444 · p margin 0 0 9px
     h3 15px · h4 13.5px — Poppins 700 #042940, margin 13px 0 7px
     ul/ol margin 0 0 9px, padding-left 24px · li margin-bottom 6px
     bullet marker: #84b026, slightly enlarged (pronounced, brand-colored)
     blockquote: 3px #84b026 left bar, rgba(132,176,38,.07) fill
     a: #0b6aa9 underlined
   Scoped under DESCRIPTION_EDITOR_CLASS so other Quill instances in the
   dashboard are untouched. Urbanist/Poppins are loaded in public/index.html
   specifically for this parity. NOTE: Quill draws its bullet via
   `ul > li::before`, the browsers draw real lists via `li::marker` — both are
   styled to the same look. */
export const DESCRIPTION_EDITOR_CLASS = "lusuku-desc-editor";

export const DESCRIPTION_TYPOGRAPHY_CSS = `
  .${DESCRIPTION_EDITOR_CLASS} .ql-container {
    font-family: "Urbanist", "Inter", "Segoe UI", sans-serif;
    font-size: 13.5px;
  }
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor {
    font-size: 13.5px;
    line-height: 1.65;
    color: #444;
    min-height: 160px;
  }
  /* Pin body copy AND list items to the same size explicitly — the dashboard
     template ships global \`li\`/\`ul\` rules that would otherwise win over the
     container's font-size and render bullets smaller than paragraphs. */
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor p,
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor li {
    font-size: 13.5px;
    line-height: 1.65;
  }
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor p { margin: 0 0 9px; }
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor h3,
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor h4 {
    font-family: "Poppins", "Urbanist", sans-serif;
    font-weight: 700;
    color: #042940;
    margin: 13px 0 7px;
  }
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor h3 { font-size: 15px; }
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor h4 { font-size: 13.5px; }
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor ul,
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor ol { margin: 0 0 9px; padding-left: 24px; }
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor li { margin-bottom: 6px; }
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor ul > li::before {
    color: #84b026;
    font-size: 1.25em;
    font-weight: 700;
  }
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor blockquote {
    border-left: 3px solid #84b026;
    margin: 10px 0;
    padding: 6px 14px;
    color: #555;
    background: rgba(132, 176, 38, 0.07);
    border-radius: 0 8px 8px 0;
  }
  .${DESCRIPTION_EDITOR_CLASS} .ql-editor a { color: #0b6aa9; text-decoration: underline; }
`;

/* ── Legacy plain-text lift ──
   Older products only have the plain `description` (bullets are literal "•"
   characters). When such a product is opened in the editor, lift the text
   into REAL semantic HTML: consecutive bullet lines ("•", "·", "-", "*")
   become <ul><li>…</li></ul> (so the editor shows true Quill bullets and the
   next save upgrades the stored data), other lines become <p>. The web
   client applies the same grouping at render time for not-yet-resaved rows
   (Lusuku_Web src/utils/descriptionHtml.js — keep in sync). */
const BULLET_LINE_RE = /^\s*[•·▪◦*-]\s+/;

const escapePlain = (line) =>
  line
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/* ── Stored-HTML normalization on editor load ──
   Mirrors Lusuku_Web src/utils/descriptionHtml.js (keep in sync): heals
   entity double-encoding left by the pre-July-2026 server sanitizer
   (`&amp;amp;` → `&amp;`) and upgrades legacy "<p>• …</p>" paragraph-bullets
   to real <ul><li> lists, so the QC/vendor editor shows true bullets and the
   next save persists the upgraded, healed HTML. */
export function normalizeStoredDescriptionHtml(html) {
  if (!html) return "";
  let cur = String(html);
  let prev;
  do {
    prev = cur;
    cur = cur.replace(/&amp;(amp|lt|gt|quot|#39|nbsp);/gi, "&$1;");
  } while (cur !== prev);

  const parts = cur.split(/(<p>[\s\S]*?<\/p>)/gi).filter(Boolean);
  const out = [];
  let items = [];
  const flush = () => {
    if (items.length) {
      out.push(`<ul>${items.map((li) => `<li>${li}</li>`).join("")}</ul>`);
      items = [];
    }
  };
  const bulletRe = /^\s*(?:•|·|▪|◦|\*|-)(?:&nbsp;|\s)+/;
  for (const part of parts) {
    const m = part.match(/^<p>([\s\S]*?)<\/p>$/i);
    if (m && bulletRe.test(m[1])) {
      items.push(m[1].replace(bulletRe, "").trim());
    } else {
      flush();
      out.push(part);
    }
  }
  flush();
  return out.join("");
}

export function liftPlainTextToHtml(text) {
  if (!text) return "";
  const out = [];
  let listItems = [];
  const flushList = () => {
    if (listItems.length) {
      out.push(`<ul>${listItems.map((li) => `<li>${li}</li>`).join("")}</ul>`);
      listItems = [];
    }
  };
  for (const rawLine of String(text).split("\n")) {
    const line = rawLine.trim();
    if (BULLET_LINE_RE.test(line)) {
      listItems.push(escapePlain(line.replace(BULLET_LINE_RE, "")));
    } else {
      flushList();
      if (line) out.push(`<p>${escapePlain(line)}</p>`);
    }
  }
  flushList();
  return out.join("");
}

/* Toolbar limited to the formats the server allowlist accepts.
   No color/size/font/image — uniform look across all vendor listings. */
export const DESCRIPTION_QUILL_MODULES = {
  toolbar: [
    [{ header: [3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    ["clean"],
  ],
};

export const DESCRIPTION_QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "blockquote",
  "link",
];

/**
 * Client-side mirror of the server's htmlToPlainText — used to send a plain
 * `description` alongside `descriptionHtml` (older backend versions ignore
 * the HTML field, so submissions degrade gracefully). Lists become "• "
 * bullets; paragraphs become newlines.
 */
export function quillHtmlToPlainText(html) {
  if (html == null) return "";
  return String(html)
    .replace(/<li[^>]*>/gi, "\n• ")
    .replace(/<\/(p|li|h[1-6]|blockquote|ol|ul)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    // &amp; decoded LAST so "&amp;lt;" correctly yields "&lt;", not "<".
    .replace(/&amp;/gi, "&")
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** True when a Quill value is effectively empty (`<p><br></p>` etc.). */
export function isQuillEmpty(html) {
  return quillHtmlToPlainText(html) === "";
}
